import { useCallback, useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cva, type VariantProps } from 'class-variance-authority';

type PopoverMenuTriggerProps = {
  onClick: () => void;
  open: boolean;
};

type PopoverMenuRenderProps = {
  close: () => void;
};

const contentVariants = cva(
  'z-popover rounded-lg border border-input bg-popover text-popover-foreground shadow-popover outline-none ' +
    'data-[state=open]:animate-in data-[state=closed]:animate-out ' +
    'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 ' +
    'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 ' +
    'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 ' +
    'data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1',
  {
    variants: {
      width: {
        sm: 'min-w-popover-sm',
        md: 'min-w-popover-md',
        lg: 'min-w-popover-lg',
      },
      padded: {
        true: 'p-1',
        false: 'p-0',
      },
    },
    defaultVariants: {
      width: 'sm',
      padded: true,
    },
  },
);

type ContentVariantProps = VariantProps<typeof contentVariants>;

export type PopoverMenuProps = ContentVariantProps & {
  /** Render the trigger. Wire `onClick` to whatever element opens the menu. */
  renderTrigger: (props: PopoverMenuTriggerProps) => ReactNode;
  children: ReactNode | ((props: PopoverMenuRenderProps) => ReactNode);
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  alignOffset?: number;
};

const DEFAULT_SIDE: PopoverMenuProps['side'] = 'bottom';
const DEFAULT_ALIGN: PopoverMenuProps['align'] = 'start';

/*
 * Anchored menu — Radix Popover with the project trigger-render pattern.
 *
 * Style is locked: `width` picks one of three canonical sizes and `padded`
 * controls the 4px outer ring (false only when the surface is a richly
 * structured composition that owns its own padding, like the profile menu).
 * The component does not accept a `className`.
 */
export function PopoverMenu({
  renderTrigger,
  children,
  side = DEFAULT_SIDE,
  align = DEFAULT_ALIGN,
  sideOffset = 4,
  alignOffset = 0,
  width,
  padded,
}: PopoverMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => {
    setOpen((o) => !o);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>{renderTrigger({ onClick: toggle, open })}</Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          className={contentVariants({ width, padded })}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
